import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { socialApi } from '@/lib/api';
import type { SocialPostStatus } from '@/types';

// ============================================
// Channels
// ============================================

export function useChannels() {
  return useQuery({
    queryKey: ['social-channels'],
    queryFn: socialApi.listChannels,
  });
}

export function useSyncChannels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: socialApi.syncChannels,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social-channels'] });
    },
  });
}

export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { isActive?: boolean } }) =>
      socialApi.updateChannel(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social-channels'] });
    },
  });
}

// ============================================
// Schedules
// ============================================

export function useChannelSchedule(channelId: string | undefined) {
  return useQuery({
    queryKey: ['social-schedule', channelId],
    queryFn: () => socialApi.getSchedule(channelId!),
    enabled: !!channelId,
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      data,
    }: {
      channelId: string;
      data: any;
    }) => socialApi.updateSchedule(channelId, data),
    onSuccess: (_, { channelId }) => {
      qc.invalidateQueries({ queryKey: ['social-schedule', channelId] });
      qc.invalidateQueries({ queryKey: ['social-channels'] });
    },
  });
}

// ============================================
// Queue
// ============================================

export function useChannelQueue(channelId: string | undefined) {
  return useQuery({
    queryKey: ['social-queue', channelId],
    queryFn: () => socialApi.getQueue(channelId!),
    enabled: !!channelId,
  });
}

// ============================================
// Posts
// ============================================

export function useSocialPosts(params?: {
  status?: SocialPostStatus;
  channelId?: string;
  take?: number;
  skip?: number;
}) {
  return useQuery({
    queryKey: ['social-posts', params],
    queryFn: () => socialApi.listPosts(params),
  });
}

export function useSocialPost(id: string | undefined) {
  return useQuery({
    queryKey: ['social-post', id],
    queryFn: () => socialApi.getPost(id!),
    enabled: !!id,
  });
}

export function useCreatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: socialApi.createPost,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social-posts'] });
      qc.invalidateQueries({ queryKey: ['social-queue'] });
    },
  });
}

export function useUpdatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      socialApi.updatePost(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social-posts'] });
      qc.invalidateQueries({ queryKey: ['social-post'] });
      qc.invalidateQueries({ queryKey: ['social-queue'] });
    },
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: socialApi.deletePost,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social-posts'] });
      qc.invalidateQueries({ queryKey: ['social-queue'] });
    },
  });
}

export function useCancelPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: socialApi.cancelPost,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social-posts'] });
      qc.invalidateQueries({ queryKey: ['social-post'] });
      qc.invalidateQueries({ queryKey: ['social-queue'] });
    },
  });
}

export function useRetryPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: socialApi.retryPost,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social-posts'] });
      qc.invalidateQueries({ queryKey: ['social-post'] });
      qc.invalidateQueries({ queryKey: ['social-queue'] });
    },
  });
}

// ============================================
// Settings
// ============================================

export function useSocialSettings() {
  return useQuery({
    queryKey: ['social-settings'],
    queryFn: socialApi.getSettings,
  });
}

export function useUpdateSocialSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: socialApi.updateSettings,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social-settings'] });
    },
  });
}

export function useTestPublerConnection() {
  return useMutation({
    mutationFn: socialApi.testConnection,
  });
}

// ============================================
// Templates
// ============================================

export function usePostTemplates() {
  return useQuery({
    queryKey: ['social', 'templates'],
    queryFn: socialApi.listTemplates,
  });
}

export function usePostTemplate(id: string) {
  return useQuery({
    queryKey: ['social', 'templates', id],
    queryFn: () => socialApi.getTemplate(id),
    enabled: !!id,
  });
}

export function useCreatePostTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: socialApi.createTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'templates'] }),
  });
}

export function useUpdatePostTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      socialApi.updateTemplate(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'templates'] }),
  });
}

export function useDeletePostTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: socialApi.deleteTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'templates'] }),
  });
}

export function useCreatePostFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: { variables: Record<string, string>; channelIds?: string[] } }) =>
      socialApi.createPostFromTemplate(templateId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'posts'] }),
  });
}
